import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, Menu, MenuItem } from '../patterns';
import { SprayProduct, SprayProgram, SprayRound, RoundProductOverride } from '../../services/sprayService';
import IdChip from './IdChip';

/**
 * Per-program inline panel that the SprayProgramList expands below each
 * program row. Provides:
 *
 *  - A top toolbar with Expand All / Collapse All buttons (round-level
 *    only, scoped to this program — spec §7.1, decision L1) and the
 *    bulk-conversion buttons (Convert Selected / Convert All — spec §2.3,
 *    decision N1).
 *  - A rounds DataTable with per-round checkboxes (selection state lives
 *    in this panel — spec §2.3 / N1) and a per-round 3-dot Menu hosting
 *    the Single Convert action (spec §2.3 / 7.2).
 *  - A per-round chevron that toggles a detail row showing the products
 *    with read-only catalog dose pairs, planned date, water rate.
 *
 * The panel itself does not call the conversion API. It emits a structured
 * event via `onRequestConvert` so the parent (SprayProgramList) can host
 * the orchard picker modal and the toast/dialog UX in one place — keeping
 * the single-vs-bulk response handling out of this component.
 *
 * Phase C2 / Task 12 — migrated from react-bootstrap <Table> + <Dropdown>
 * to pat-* DataTable + Menu primitives. The architectural fork chosen here
 * is DataTable + expandedRowRender (vs a list of RowCards) because RowCard
 * has no `children` slot for the expansion body; extending RowCard would be
 * a primitive-level change that should land separately.
 */
export type ConvertScope =
  | { kind: 'single'; roundId: string; roundLabel: string }
  | { kind: 'selected'; roundIds: string[]; count: number }
  | { kind: 'all'; count: number };

interface Props {
  program: SprayProgram;
  products: SprayProduct[];
  onRequestConvert: (scope: ConvertScope) => void;
}

interface RoundRow {
  rid: string;
  round: SprayRound;
  idx: number;
  label: string;
  productIds: string[];
}

const roundDisplayLabel = (r: SprayRound, idx: number): string =>
  r.name || `Round ${idx + 1}`;

/**
 * Round.products is now `Array<string | RoundProductOverride>` after the
 * product-schema-redesign track. The panel displays product IDs for lookup
 * + as React keys, so unwrap each entry to a plain string ID.
 */
const unwrapProductId = (entry: string | RoundProductOverride): string =>
  typeof entry === 'string' ? entry : entry.productId;

const SprayProgramRoundsPanel: React.FC<Props> = ({ program, products, onRequestConvert }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rounds = program.rounds || [];

  // Per-round expansion + selection. Both are panel-local — they reset
  // when the parent unmounts the panel (i.e. when the program row is
  // collapsed back into the table).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const productMap = useMemo(
    () => new Map(products.map(p => [p._id, p])),
    [products]
  );

  const toggleExpand = (roundId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(roundId)) next.delete(roundId);
      else next.add(roundId);
      return next;
    });
  };

  const expandAll = () => {
    setExpanded(new Set(rounds.map(r => r._id!).filter(Boolean)));
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  const toggleSelected = (roundId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(roundId)) next.delete(roundId);
      else next.add(roundId);
      return next;
    });
  };

  const handleConvertSelected = () => {
    if (selected.size === 0) return;
    onRequestConvert({
      kind: 'selected',
      roundIds: Array.from(selected),
      count: selected.size,
    });
  };

  const handleConvertAll = () => {
    onRequestConvert({ kind: 'all', count: rounds.length });
  };

  const handleConvertSingle = (round: SprayRound, idx: number) => {
    if (!round._id) return;
    onRequestConvert({
      kind: 'single',
      roundId: round._id,
      roundLabel: roundDisplayLabel(round, idx),
    });
  };

  if (rounds.length === 0) {
    return (
      <div className="text-muted small p-2">
        {t('spray.programs.noRounds')}
      </div>
    );
  }

  // Build the row-shape DataTable consumes. Resolve products via the
  // productList virtual when available, else fall back to the local
  // products array (forms hand us unsynced data), else legacy productId.
  const rows: RoundRow[] = rounds.map((r, idx) => {
    const rid = r._id || `idx-${idx}`;
    const rawEntries: Array<string | RoundProductOverride> =
      (r.productList && r.productList.length > 0)
        ? r.productList
        : (r.products && r.products.length > 0)
          ? r.products
          : (r.productId ? [r.productId] : []);
    const productIds = rawEntries.map(unwrapProductId);
    return { rid, round: r, idx, label: roundDisplayLabel(r, idx), productIds };
  });

  return (
    <div className="px-3 py-2 bg-light border-top spray-rounds-panel" data-testid="rounds-panel">
      {/* Per-program-card top toolbar. Bulk actions scope to this program
          only (spec §2.3 / N1). Convert Selected disables when no rounds
          are picked. */}
      <div className="d-flex flex-wrap gap-2 mb-2">
        <button type="button" className="pat-btn pat-btn--subtle" onClick={expandAll}>
          {t('spray.programs.expandAllRounds')}
        </button>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={collapseAll}>
          {t('spray.programs.collapseAllRounds')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          onClick={handleConvertSelected}
          disabled={selected.size === 0}
        >
          {t('spray.programs.convertSelected', { count: selected.size })}
        </button>
        <button type="button" className="pat-btn pat-btn--primary" onClick={handleConvertAll}>
          {t('spray.programs.convertAll', { count: rounds.length })}
        </button>
      </div>

      <DataTable<RoundRow>
        rows={rows}
        rowKey={(row) => row.rid}
        expandedRowKey={expanded}
        expandedRowRender={(row) => {
          const list = row.productIds;
          return (
            <div className="small" data-testid={`round-detail-${row.rid}`}>
              <div>
                <strong>{t('spray.programs.roundProduct')}:</strong>{' '}
                {list.length === 0 ? '—' : null}
              </div>
              {list.length > 0 && (
                <ul className="list-unstyled mb-1 ms-3">
                  {list.map(pid => {
                    const p = productMap.get(pid);
                    const dose = p?.dosePer100L;
                    const unit = p?.dosePer100LUnit;
                    const dosePair = (dose != null && unit) ? `${dose} ${unit}/100L` : '—';
                    return (
                      <li key={pid}>
                        <span>{p?.productName || pid}</span>
                        <span className="text-muted ms-2">{dosePair}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div>
                <strong>{t('spray.programs.roundWater')}:</strong>{' '}
                {row.round.waterPerHectare ?? '—'}
              </div>
              <div className="text-muted small">
                {t('spray.programs.roundProductsListedHint')}
              </div>
            </div>
          );
        }}
        columns={[
          {
            key: 'select',
            label: '',
            render: (row) => (
              <input
                type="checkbox"
                className="pat-checkbox"
                aria-label={t('spray.programs.selectRound', { name: row.label })}
                checked={selected.has(row.rid)}
                onChange={() => toggleSelected(row.rid)}
                // Selection only makes sense when the round actually has
                // an _id (newly-added rounds before save don't).
                disabled={!row.round._id}
              />
            ),
          },
          {
            key: 'expand',
            label: '',
            render: (row) => {
              const isExpanded = expanded.has(row.rid);
              return (
                <button
                  type="button"
                  className="pat-menu__trigger"
                  aria-label={t(isExpanded ? 'spray.programs.collapseRound' : 'spray.programs.expandRound', { name: row.label })}
                  onClick={() => toggleExpand(row.rid)}
                >
                  {isExpanded ? '▾' : '▸'}
                </button>
              );
            },
          },
          {
            key: 'name',
            label: t('spray.programs.roundName'),
            render: (row) => (
              <>
                {row.label}
                <IdChip prefix="R" seq={row.round.seq} />
              </>
            ),
          },
          {
            key: 'date',
            label: t('spray.programs.roundDate'),
            render: (row) => row.round.plannedDate?.substring(0, 10) ?? null,
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
              <Menu
                placement="bottom-end"
                trigger={
                  <button
                    type="button"
                    className="pat-menu__trigger"
                    aria-label={t('spray.programs.roundActions', { name: row.label })}
                  >
                    &#8942;
                  </button>
                }
              >
                <MenuItem
                  onClick={() => handleConvertSingle(row.round, row.idx)}
                  disabled={!row.round._id}
                >
                  {t('spray.programs.convertRound')}
                </MenuItem>
                {row.round._id && (
                  <MenuItem
                    onClick={() => navigate(`/protection/sessions?programId=${program._id}&roundId=${row.round._id}`)}
                  >
                    {t('spray.programs.viewRoundSessions')}
                  </MenuItem>
                )}
              </Menu>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SprayProgramRoundsPanel;
